import type {SanitizedAPIError} from '../Logger'
import {
  CASB_PROVIDER_FILE_LIVE_PATH,
  CUSTOM_CATEGORIES_PATH,
  DLP_DOPAMINE_FILE_HASH_PATH,
  GET_TOKENS_PATH,
  REFRESH_TOKEN_PATH,
} from './apiErrorIgnorePathConstants'

/**
 * A single ignore rule for an expected backend response. A rule matches when
 * the status matches AND every provided discriminator (code/message/path)
 * matches. `code` is the most reliable signal; message/path matching is
 * brittle and only used because the backend exposes no error-code contract yet.
 */
type IgnoreRule = {
  status: number | number[]
  code?: string
  messagePattern?: RegExp
  pathPattern?: RegExp
  reason: string
}

// Expected backend responses that are deliberate product/validation/auth states
// rather than real frontend failures. Each entry is pinned to a cloud-backend
// source line. Only ERR_NOT_ENTITLED is a stable machine code; the rest depend
// on free-form message text and are inherently brittle. A backend-owned
// error-code contract is tracked as a follow-up so these can become code-based.
const IGNORE_RULES: IgnoreRule[] = [
  {
    status: 400,
    code: 'ERR_NOT_ENTITLED',
    pathPattern: /\/policies\/[^/]+\/dlp\/(restrictions|exceptions)$/,
    reason:
      'DLP entitlement denial on restrictions/exceptions routes (dlp_routes.py:292,366).',
  },
  {
    status: 400,
    code: 'ERR_NOT_ENTITLED',
    pathPattern: DLP_DOPAMINE_FILE_HASH_PATH,
    reason:
      'DLP Dopamine entitlement denial (GET /fe-violations/file-hash/{fileHash}/dopamine; #3692).',
  },
  {
    status: 400,
    messagePattern: /already (exists|assigned)/i,
    reason:
      'Conflicts: Policy/Custom category already exists; User/Group already ' +
      'assigned (policy_management_routes.py:107, custom_category_routes.py:131, ' +
      'assignment_routes.py:165)',
  },
  {
    status: 400,
    messagePattern: /is a reserved policy name$/i,
    reason: 'Reserved policy name (policy_management_routes.py:81)',
  },
  {
    status: 400,
    messagePattern: /^Policy not found$/,
    reason: 'Expected POLICY_NOT_FOUND_ERR_RESPONSE (common.py:36)',
  },
  {
    status: 400,
    messagePattern: /^Invalid value for 'after' query parameter$/,
    reason: 'Pagination cursor validation (common.py:48)',
  },
  {
    status: 400,
    messagePattern: /^Invalid URLs found$/,
    pathPattern: CUSTOM_CATEGORIES_PATH,
    reason:
      'Custom category URL validation (url_routes.py make_invalid_url_response; ' +
      '#3486 / DOPECONSOLE-6Y)',
  },
  {
    status: 400,
    messagePattern: /^Custom category not found$/,
    pathPattern: CUSTOM_CATEGORIES_PATH,
    reason:
      'Missing custom category on GET/PUT/DELETE (custom_category_routes.py, ' +
      'url_routes.py, db.py CATEGORY_DATA_NOT_FOUND; #3486 / DOPECONSOLE-6F)',
  },
  {
    status: 404,
    messagePattern: /^Requested resource not found$/,
    pathPattern: /\/neural\//,
    reason:
      'CASB /neural/* file or app not in DB (api-error-responses.md CASB table; ' +
      'api_lambda/common.py:51-54)',
  },
  {
    status: 404,
    messagePattern: /^User does not exist$/,
    pathPattern: /\/get-associated-users$/,
    reason:
      'Endpoints no associated users (api-error-responses.md jetbridge endpoints; ' +
      'get_associated_users.py:80)',
  },
  {
    status: 404,
    messagePattern: /^No recategorization request found$/,
    pathPattern: /\/categories\/recat\/?(?:\?|$)/,
    reason:
      'No pending/approved recategorization for URL (category-service ' +
      'recat_api.py NO_RECAT_REQUEST_ERROR; #3486 / DOPECONSOLE-F6)',
  },
]

/** Returns whether the HTTP status satisfies a rule's single status or status list. */
const isStatusMatch = (rule: IgnoreRule, status?: number): boolean => {
  if (status === undefined) return false
  return Array.isArray(rule.status)
    ? rule.status.includes(status)
    : rule.status === status
}

/**
 * Expected session-expiry failures on POST /refresh-tokens only.
 * Frontend calls this route exclusively via instance.post (axios-config.ts);
 * refresh_tokens lambda returns 400 (invalid/expired token) or 500 (recreation).
 * No-status failures on this route (any axiosCode) are ignored here.
 * Allowlisted ERR_NETWORK / ECONNABORTED ignore is handled by isClientNetworkFailure.
 */
const isRefreshTokenFailure = (error: SanitizedAPIError): boolean => {
  if (!REFRESH_TOKEN_PATH.test(error.requestUrl ?? '')) return false
  if (error.httpMethod !== 'POST') return false
  return error.httpStatus === undefined || error.httpStatus === 400
}

/**
 * Expected login-callback failures on GET /get-tokens (get_tokens.py).
 * Called from /callback and switch-account flows (AuthService.getTokens).
 * Product messages only — "Invalid or no token was given to the API" stays
 * captured: backend uses that for empty and invalid/expired temp JWT alike,
 * so ignoring it would hide a possible client-side empty-token call.
 * "Tenant details are missing" also stays captured (DynamoDB consistency).
 */
const GET_TOKENS_CALLBACK_MESSAGES = new Set([
  'Invalid domain requested',
  'Admin does not exist',
])

const isGetTokensCallbackFailure = (error: SanitizedAPIError): boolean => {
  if (!GET_TOKENS_PATH.test(error.requestUrl ?? '')) return false
  if (error.httpMethod !== 'GET') return false
  if (error.httpStatus !== 400) return false
  return GET_TOKENS_CALLBACK_MESSAGES.has(error.backendErrorMessage ?? '')
}

/** Session-expiry messages returned with 401/403 on flightdeck routes (AuthConst). */
const SESSION_AUTH_MESSAGES = new Set(['Forbidden', 'Invalid token.'])

/**
 * Detects session/auth expiry: bare 401/403 or known session-auth messages.
 * These are expected when tokens expire and should not be reported to Sentry.
 */
const isSessionAuthFailure = (error: SanitizedAPIError): boolean => {
  const {httpStatus, backendErrorCode, backendErrorMessage} = error
  if (httpStatus !== 401 && httpStatus !== 403) return false
  if (backendErrorCode) return false
  if (!backendErrorMessage) return true
  return SESSION_AUTH_MESSAGES.has(backendErrorMessage)
}

/** Axios codes for client-side connectivity failures (no HTTP response). */
const CLIENT_NETWORK_AXIOS_CODES = new Set(['ERR_NETWORK', 'ECONNABORTED'])

type ClientNetworkIgnoreRoute = {
  pathPattern: RegExp
  /** When set, only these HTTP methods are ignored. Omit = any method. */
  methods?: ReadonlySet<string>
}

/**
 * Known-noisy routes for no-status ERR_NETWORK / ECONNABORTED (prefetch and
 * polling/background calls). Scoped so CORS/DNS regressions on new routes
 * still reach Sentry. Shared read/write paths (policies, SIEM integrations,
 * creds) are GET-only so save/delete failures stay visible.
 */
const CLIENT_NETWORK_ROUTES: ClientNetworkIgnoreRoute[] = [
  {pathPattern: /\/notifications\/?$/},
  {pathPattern: /\/entitlements\/?$/},
  {pathPattern: /\/get-import-status\/?$/},
  {pathPattern: /\/fe-violations\//},
  {pathPattern: /\/tenant\/config\/?$/},
  /*
   * Endpoints list (`/endpoints/search`) and analytics location search on the
   * endpoints host (`…/search` → pathname `/search`; DOPECONSOLE-80).
   * Anchored at pathname start so `/foo/search` stays visible.
   */
  {pathPattern: /^\/(?:endpoints\/)?search\/?$/},
  // Endpoint metadata (GetEndpointMetadata); not under /endpoints/.
  {pathPattern: /\/aggregations\/?$/},
  {
    pathPattern: /\/(?:v1\/)?policies(?:\/|$)/,
    methods: new Set(['GET']),
  },
  {pathPattern: /\/neural\//},
  {pathPattern: /\/get-block-pages\/?$/},
  {pathPattern: /\/get-cac\/?$/},
  {pathPattern: /\/get-admins\/?$/},
  {pathPattern: /\/get-diagnostics-results\/?$/},
  {pathPattern: /\/get-associated-users\/?$/},
  {pathPattern: /\/roles\/?$/},
  {pathPattern: /\/overview\/?$/},
  {
    pathPattern: /\/integrations\/(s3|http)\/?$/,
    methods: new Set(['GET']),
  },
  {pathPattern: /\/search-users-groups\/?$/},
  {pathPattern: /\/subscription\/?$/},
  {
    pathPattern: /\/creds\/?$/,
    methods: new Set(['GET']),
  },
  // Analytics AI Usage reads (DOPECONSOLE-GX). Exclude /ai-usage/export —
  // that is a user-triggered download and must stay reportable on ERR_NETWORK.
  {
    pathPattern: /^\/ai-usage\/(?!export(?:\/|$))/,
    methods: new Set(['GET']),
  },
  // Flightdeck endpoints search (DOPECONSOLE-69 / 7T). Distinct from
  // `/endpoints/search` and `/search`; `/v1/search` stays captured.
  {
    pathPattern: /^\/v1\/endpoints\/search\/?$/,
    methods: new Set(['GET']),
  },
  // GET-only: PUT /endpoint-settings is a settings save.
  {pathPattern: /\/endpoint-settings\/?$/, methods: new Set(['GET'])},
  // Account list on nav mount (getAccountsList; DOPECONSOLE-6V / EA). The
  // expired-token account switch (onAccountSwitch) logs out on failure and
  // reports network errors itself.
  {pathPattern: /\/switch-account\/?$/, methods: new Set(['GET'])},
  {pathPattern: /\/get-users\/?$/, methods: new Set(['POST'])},
  {pathPattern: /\/get-groups\/?$/, methods: new Set(['POST'])},
  {pathPattern: /\/policies\/clashes\/?$/, methods: new Set(['POST'])},
  {pathPattern: /\/violation\/?$/, methods: new Set(['GET'])},
  {pathPattern: /\/shadow-it\/apps\/?$/, methods: new Set(['GET'])},
  {
    pathPattern: /\/productivity\/(activity-time|top-categories)\/?$/,
    methods: new Set(['GET']),
  },
]

/**
 * Pathname for route matching. Accepts absolute or relative Axios URLs and
 * strips query strings so allowlist regexes can stay path-only.
 */
const getRequestPathname = (requestUrl: string): string => {
  if (!requestUrl) return ''
  try {
    return new URL(requestUrl, 'https://placeholder.local').pathname
  } catch {
    return requestUrl.split('?', 1)[0] ?? ''
  }
}

/**
 * Client connectivity blips on allowlisted routes: no HTTP status, Axios reports
 * ERR_NETWORK or ECONNABORTED. Not actionable frontend defects; still surfaces
 * in UI via normal reject paths.
 */
const isClientNetworkFailure = (error: SanitizedAPIError): boolean => {
  if (error.httpStatus !== undefined) return false
  if (!CLIENT_NETWORK_AXIOS_CODES.has(error.axiosCode ?? '')) return false
  const pathname = getRequestPathname(error.requestUrl ?? '')
  const method = (error.httpMethod ?? '').toUpperCase()
  return CLIENT_NETWORK_ROUTES.some(({pathPattern, methods}) => {
    if (!pathPattern.test(pathname)) return false
    if (methods && !methods.has(method)) return false
    return true
  })
}

const isCasbProviderFileLiveServerError = (
  error: SanitizedAPIError,
): boolean => {
  if (error.httpStatus !== 500) return false
  if ((error.httpMethod ?? '').toUpperCase() !== 'GET') return false
  return CASB_PROVIDER_FILE_LIVE_PATH.test(error.requestUrl ?? '')
}

const matchesIgnoreCatalogRule = (error: SanitizedAPIError): boolean => {
  const {httpStatus, backendErrorCode, backendErrorMessage, requestUrl} = error
  return IGNORE_RULES.some(rule => {
    if (!isStatusMatch(rule, httpStatus)) return false
    if (rule.code && backendErrorCode !== rule.code) return false
    if (
      rule.messagePattern &&
      !rule.messagePattern.test(backendErrorMessage ?? '')
    ) {
      return false
    }
    if (rule.pathPattern && !rule.pathPattern.test(requestUrl ?? '')) {
      return false
    }
    return true
  })
}

/**
 * Returns true when an API error is an expected backend product/validation/auth
 * response that should NOT be reported to Sentry.
 *
 * Hard guarantees:
 * - 5xx is never ignored, except GET CASB google/microsoft files/.../live 500s
 *   (provider/upstream failures).
 * - Bare 401/403 and session-auth messages ("Forbidden", "Invalid token.")
 *   are ignored (session expiry).
 * - GET /get-tokens 400 with known callback messages is ignored;
 *   "Tenant details are missing" is still captured.
 * - Catalog-backed 404 message+path pairs are ignored; other 404s are captured.
 * - Client network/timeout (ERR_NETWORK / ECONNABORTED, no HTTP status) is
 *   ignored only on known-noisy allowlisted paths. Other no-status failures
 *   (e.g. cancel) and unlisted routes are still captured.
 */
export const shouldIgnoreAPIError = (error: SanitizedAPIError): boolean => {
  const {httpStatus} = error

  if (isCasbProviderFileLiveServerError(error)) return true

  const isServerFailure = httpStatus !== undefined && httpStatus >= 500
  if (isServerFailure) return false

  const isExpectedAuthOrSessionFailure =
    isRefreshTokenFailure(error) ||
    isGetTokensCallbackFailure(error) ||
    isSessionAuthFailure(error)
  const isAllowlistedClientNetworkFailure = isClientNetworkFailure(error)

  if (isExpectedAuthOrSessionFailure || isAllowlistedClientNetworkFailure) {
    return true
  }

  return matchesIgnoreCatalogRule(error)
}
